import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ShieldAlert } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { useGetStageGateCheck } from "@/hooks/capitalMarkets/useSpvPools";
import type { PoolInFlightDto } from "@/types/capitalMarketsDashboard";
import { humanizeStatus } from "../shared/format";

interface Props {
  pools: PoolInFlightDto[];
  loading: boolean;
}

/**
 * SPV Pool v1.0 §4.7 / PR 9 — collapsed-by-default widget listing every
 * non-terminal pool blocked on stage-gate certifications, with the missing
 * gates named (resolved via PR 8's guard). Each gate links to the pool's
 * Certifications tab.
 */
export function PoolsBlockedOnGates({ pools, loading }: Props) {
  const [expanded, setExpanded] = useState(false);
  const blocked = pools.filter((p) => p.nextStageMissingGateCount > 0);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex items-center gap-space-2 w-full text-left"
      >
        {expanded ? (
          <ChevronDown size={16} strokeWidth={2} aria-hidden />
        ) : (
          <ChevronRight size={16} strokeWidth={2} aria-hidden />
        )}
        <ShieldAlert
          size={16}
          strokeWidth={1.5}
          className="text-gold-shadow"
          aria-hidden
        />
        <h2 className="font-display font-bold text-h3 m-0">
          Pools Blocked on Gates
        </h2>
        <span className="ml-auto inline-flex items-center px-space-2 py-[2px] rounded-sm bg-gold text-onyx font-display text-[10px] font-bold uppercase tracking-[0.16em]">
          {blocked.length}
        </span>
      </button>

      {expanded && (
        <div className="mt-space-3 flex flex-col gap-space-3" data-testid="blocked-pools-body">
          {loading && (
            <p className="text-small text-gray-mid italic m-0">Loading…</p>
          )}
          {!loading && blocked.length === 0 && (
            <p className="text-small text-gray-mid italic m-0">
              No pools are blocked — every in-flight pool is ready to advance.
            </p>
          )}
          {!loading &&
            blocked.map((p) => <BlockedPoolRow key={p.id} pool={p} />)}
        </div>
      )}
    </Card>
  );
}

function BlockedPoolRow({ pool }: { pool: PoolInFlightDto }) {
  const check = useGetStageGateCheck(pool.id, pool.nextStage ?? undefined);
  const gates = [
    ...(check.data?.missing ?? []),
    ...(check.data?.revoked ?? []),
  ];

  return (
    <div className="border-t border-border-light pt-space-2 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between gap-space-2">
        <Link
          to={`/capital-markets/pools/${pool.id}`}
          className="font-semibold text-small hover:text-gold-shadow"
        >
          {pool.poolName}
        </Link>
        <span className="text-caption text-gray-mid">
          {pool.nextStage
            ? `→ ${humanizeStatus(pool.nextStage)}`
            : "terminal stage"}
        </span>
      </div>
      {check.isLoading && (
        <p className="text-caption text-gray-mid italic m-0 mt-space-1">
          Resolving gates…
        </p>
      )}
      {!check.isLoading && (
        <ul className="m-0 mt-space-1 p-0 list-none flex flex-wrap gap-space-2">
          {gates.length === 0 && (
            <li className="text-caption text-gray-mid">
              {pool.nextStageMissingGateCount} gate(s) open.
            </li>
          )}
          {gates.map((g) => (
            <li key={g.certificationDefinitionId}>
              <Link
                to={`/capital-markets/pools/${pool.id}?tab=certifications`}
                className="inline-flex items-center px-space-2 py-[2px] rounded-sm border border-border-light text-caption hover:border-gold hover:text-gold-shadow"
              >
                {g.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
